import { takeEvery, takeLatest, put, call, fork } from "redux-saga/effects";
import { message, notification } from "antd";
import moment from "moment";
import { CaseService } from "services";
import {
    fetchedCases,
    failedFetchCases,
    approvedCase,
    failedApproveCase,
    proceedCaseSucceed,
    proceedCaseFailed,
    rejectCaseSucceed,
    rejectCaseFailed,
    referCaseSucceed,
    referCaseFailed,
    assignCaseSucceed,
    assignCaseFailed,
    reassignCaseSucceed,
    reassignCaseFailed,
    fetchBucketCases,
    createCaseNote,
    holdCaseSucceed,
    holdCaseFailed,
    openHoldCaseSucceed,
    openHoldCaseFailed,
    fetchCaseDetail
} from "actions";
import { history } from "router";
import { CASE_ACTIONS, UPDATE_STAGE_CASE, BUCKET_LIST_DEFAULT_PAGE_SIZE } from "constants";
import { CaseNoteRecord } from "records";

function inboxMyCasesSearch(stageId) {
    const view = Number(stageId) === 17 ? "all" : "assigned";
    return `?documents=all&stage=${stageId}&view=${view}`;
}

function replaceLocationToInboxMyCases(payload) {
    const loc = history.location || {};
    history.replace({
        pathname: "/app/inbox",
        search: inboxMyCasesSearch(payload.stageId),
        state: {
            ...(typeof loc.state === "object" && loc.state !== null
                ? loc.state
                : {}),
            case_actions: {},
            disableActionButton: false
        }
    });
}

function* caseFetchHandler({ payload }) {
    try {
        const { data, error } = yield call(CaseService.current, payload.caseStatus);
        if (!error) {
            yield put(fetchedCases(payload.caseStatus, data));
        } else {
            yield put(failedFetchCases(payload.caseStatus, error));
        }
    } catch(error) {
        yield put(failedFetchCases(payload.caseStatus, error));
    }
}

function* approveCaseHandler({ payload }) {
    try {
        message.info("Approving Case", 1);
        const { error: sanctionError } = yield call(
            CaseService.updateSanctionLimit,
            payload.case_id,
            payload.caseDetail.sanction_values
        );
        if (sanctionError) {
            message.error("Can't update the case right now", 3);
            return;
        }
        const { data, error } = yield call(
            CaseService.upgradeProceedCase,
            payload.case_task_id,
            payload
        );
        if (!error) {
            message.success("Case Approved", 3);
            yield put(
                approvedCase(
                    payload.case_task_id,
                    payload.case_id,
                    payload.this_stage,
                    payload.this_user,
                    data
                )
            );
            yield put(createCaseNote(
                payload.case_id,
                null,
                new CaseNoteRecord({
                    user: payload.user,
                    case: payload.case_id,
                    stage: payload.stage,
                    notes: payload.caseDetail.sanction_remarks,
                    create_date: moment()
                })
            ));
            yield put(fetchBucketCases(JSON.stringify([payload.stageId]), "assigned", 1, BUCKET_LIST_DEFAULT_PAGE_SIZE, payload.stageName, "INBOX", {}, null));
            history.replace("/app/inbox");
        } else {
            message.warning("Case can't be sent to user. Failed", 3);
            yield put(failedApproveCase(payload.case_task_id, error));
        }
    } catch(error) {
        message.warning("Case can't be sent to user. Failed", 3);
        yield put(failedApproveCase(payload.case_task_id, error));
    }
}

function* proceedCaseHandler({ payload }) {
    try {
        message.info("Proceeding with Case", 1);
        const { data, error } = yield call(
            CaseService.upgradeProceedCase,
            payload.case_task_id,
            payload
        );
        if (!error) {
            message.success("Case Sent to new user", 3);
            yield put(
                proceedCaseSucceed(
                    payload.case_task_id,
                    payload.case_id,
                    payload.this_stage,
                    payload.this_user,
                    payload.remarks,
                    data
                )
            );
            yield put(fetchBucketCases(JSON.stringify([payload.stageId]), payload.stageId == 2 ? "all" : "assigned", 1, BUCKET_LIST_DEFAULT_PAGE_SIZE, payload.stageName, "INBOX", {}, null));
            history.replace("/app/inbox");
        } else {
            message.warning("Case can't be sent to user. Failed", 3);
            yield put(
                proceedCaseFailed(payload.case_task_id, payload.case_id, error)
            );
        }
    } catch(error) {
        message.warning("Could not move case, Some Server issue", 3);
        yield put(
            proceedCaseFailed(payload.case_task_id, payload.case_id, error)
        );
    }
}

function* referCaseHandler({ payload }) {
    try {
        message.info("Refering Case", 1);
        const { data, error } = yield call(
            CaseService.upgradeReferCase,
            payload.case_task_id,
            payload
        );

        if (!error) {
            message.success("Case Refered", 3);
            yield put(
                referCaseSucceed(
                    payload.this_stage,
                    payload.this_user,
                    payload.remarks,
                    data
                )
            );
            yield put(fetchBucketCases(JSON.stringify([payload.stageId]), "assigned", 1, BUCKET_LIST_DEFAULT_PAGE_SIZE, payload.stageName, "INBOX", {}, null));
            history.replace("/app/inbox");
        } else {
            message.warning("Case can't be sent to user. Failed", 3);
            yield put(referCaseFailed(error));
        }
    } catch(error) {
        message.warning("Case can't be sent to user. Failed", 3);
        yield put(referCaseFailed(error));
    }
}

function* rejectCaseHandler({ payload }) {
    try {
        message.info("Rejecting Case", 1);
        const { data, error } = yield call(
            CaseService.upgradeRejectCase,
            payload.case_id,
            payload.reason,
            payload.remarks
        );
        if (!error) {
            message.info("Case Rejected", 1);
            yield put(rejectCaseSucceed(payload.case_id, payload.remarks, data));
            yield put(fetchCaseDetail(payload.case_id));
            replaceLocationToInboxMyCases(payload);
        } else {
            message.warning("Case can't be rejected. Failed", 3);
            yield put(rejectCaseFailed(error));
        }
    } catch(error) {
        error.message?.[0] ? message.error(error.message?.[0]) : message.warning("Case can't be rejected. Failed", 3);
        yield put(rejectCaseFailed(error));
    }
}

function* holdCaseHandler({ payload }) {
    try {
        message.info("Putting Case on Hold", 1);
        const { data, error } = yield call(
            CaseService.holdCase,
            payload.case_id,
            payload.remarks,
            payload.action_reason
        );
        if (!error) {
            message.info("Case Put on Hold", 1);
            yield put(holdCaseSucceed(payload.case_id, payload.remarks, data));
            yield put(fetchBucketCases(JSON.stringify([payload.stageId]), "assigned", 1, BUCKET_LIST_DEFAULT_PAGE_SIZE, payload.stageName, "INBOX", {}, null));
            history.replace("/app/inbox");
        } else {
            message.warning("Case can't be put on hold. Failed", 3);
            yield put(holdCaseFailed(error));
        }
    } catch(error) {
        message.warning("Case can't be put on hold. Failed", 3);
        yield put(holdCaseFailed(error));
    }
}

function* openHoldCaseHandler({ payload }) {
    try {
        message.info("Changing case from Hold to Open", 1);
        const { data, error } = yield call(
            CaseService.openHoldCase,
            payload.case_id,
            payload.remarks
        );
        if (!error) {
            message.info("Case Opened", 1);
            yield put(openHoldCaseSucceed(payload.case_id, payload.remarks, data));
            yield put(fetchCaseDetail(payload.case_id));
            yield put(fetchBucketCases(JSON.stringify([payload.stageId]), payload.selectedViewType, 1, BUCKET_LIST_DEFAULT_PAGE_SIZE, payload.stageName, "INBOX", {}, null));
            // history.replace("/app/inbox");
        } else {
            message.warning("Case can't be Open. Failed", 3);
            // message.error()
            yield put(openHoldCaseFailed(error));
        }
    } catch(error) {
        if(error && error.open_remarks) {
            message.error("Open Remarks Cannot be blank", 2);
        } else if(
            error && error.non_field_errors
        ) {
            error && error.non_field_errors.map(_ => {
                message.error(_, 2);
            });
        } else {
            message.warning("Case can't be Open. Failed", 3);
            yield put(openHoldCaseFailed(error));
        }
    }
}

function* assignCaseHandler({ payload }) {
    try {
        message.info("Assigning Case", 1);
        const { data, error } = yield call(
            CaseService.upgradeAssignCase,
            payload.case_task_id,
            payload
        );
        if (!error) {
            message.success("Case Assigned", 3);
            yield put(
                assignCaseSucceed(
                    payload.case_task_id,
                    payload.case_id,
                    payload.this_stage,
                    payload.this_user,
                    payload.remarks,
                    data
                )
            );
            yield put(fetchCaseDetail(payload.caseId));
            yield put(fetchBucketCases(JSON.stringify([payload.stageId]), payload.stageType, 1, 50, payload.stageName, "INBOX", true));
            // 
            // history.replace("/app/inbox");
        } else {
            message.warning("Case can't be assigned to user", 3);
            yield put(
                assignCaseFailed(payload.case_task_id, payload.case_id, error)
            );
        }
    } catch(error) {
        message.warning("Case can't be assigned to user", 3);
        yield put(
            assignCaseFailed(payload.case_task_id, payload.case_id, error)
        );
    }
}

function* reassignCaseHandler({ payload }) {
    try {
        message.info("Reassigning Case", 1);
        const { data, error } = yield call(
            CaseService.upgradeReassignCase,
            payload.case_task_id,
            payload
        );
        if (!error) {
            message.success("Case Reassigned", 3);
            yield put(
                reassignCaseSucceed(
                    payload.case_task_id,
                    payload.case_id,
                    payload.this_stage,
                    payload.this_user,
                    payload.remarks,
                    data
                )
            );
            
            yield put(fetchCaseDetail(payload.caseId));
            replaceLocationToInboxMyCases(payload);
        } else {
            message.warning("Case can't be assigned to user", 3);
            yield put(
                reassignCaseFailed(payload.case_task_id, payload.case_id, error)
            );
        }
    } catch(error) {
        message.warning("Case can't be assigned to user", 3);
        yield put(
            reassignCaseFailed(payload.case_task_id, payload.case_id, error)
        );
    }
}

function* moreCaseFetchHandler({ payload }) {
    try {
        const { data, error } = yield call(CaseService.next, payload.caseStatus);
        if (!error) {
            yield put(fetchedCases(payload.caseStatus, data.results));
        } else {
            yield put(failedFetchCases(payload.caseStatus, error));
        }
    } catch(error) {
        yield put(failedFetchCases(payload.caseStatus, error));
    }
}

function* bulkCaseProceed({ payload }) {
    message.info("Proceeding with Cases", 1);
    try {
        const { data, error } = yield call(
            CaseService.bulkApprove,
            payload.case_task_id,
            payload.this_stage,
            payload.this_user,
            payload.remarks
        );
        if (!error) {
            message.success("Cases Sent to new user", 3);
            yield put(
                proceedCaseSucceed(
                    payload.case_task_id,
                    payload.case_id,
                    payload.this_stage,
                    payload.this_user,
                    data
                )
            );
            yield put(fetchBucketCases(JSON.stringify([payload.stageId]), payload.stageId == 2 ? "all" : "assigned", 1, BUCKET_LIST_DEFAULT_PAGE_SIZE, payload.stageName, "INBOX", {}, null));
        } else {
            message.warning("Case can't be sent to user. Failed", 3);
            yield put(proceedCaseFailed(payload.case_task_id, error));
        }
    } catch(error) {
        message.warning("Case can't be sent to user. Failed", 3);
        yield put(proceedCaseFailed(payload.case_task_id, error));
    }
}

function* bulkCaseRefer({ payload }) {
    const { data, error } = yield call(
        CaseService.bulkReferCase,
        payload.case_task_id,
        payload
    );

    if (!error) {
        yield put(
            referCaseSucceed(
                payload.this_stage,
                payload.this_user,
                payload.reassignment_reason,
                data
            )
        );
    } else {
        yield put(referCaseFailed(error));
    }
}

function* bulkCaseReject({ payload }) {
    const { data, error } = yield call(
        CaseService.bulkReject,
        payload.case_id,
        payload.rejection_reason,
        payload.rejection_remarks
    );
    if (!error) {
        yield put(
            rejectCaseSucceed(
                payload.case_id,
                payload.rejection_reason,
                payload.rejection_remarks,
                data
            )
        );
        if (payload.stageId != null) {
            replaceLocationToInboxMyCases(payload);
        }
    } else {
        yield put(rejectCaseFailed(error));
    }
}


function* downloadCasesXlsx({ payload: { filters } }) {
    try {
        message.loading("Cases Export in Progress");
        yield call(
            CaseService.downloadCasesXlsx,
            filters
        );
        notification.success({
            message: "Export Complete",
            description: "Please check your email"
        });
    } catch(error) {
        message.error("Export operation couldn't be completed");
    }
}

export default fork(function*() {
    yield takeEvery(CASE_ACTIONS.FETCH_CASES, caseFetchHandler);
    yield takeEvery(CASE_ACTIONS.FETCH_MORE_CASES, moreCaseFetchHandler);
    yield takeLatest(UPDATE_STAGE_CASE.APPROVE_CASE, approveCaseHandler);
    yield takeEvery(UPDATE_STAGE_CASE.PROCEED_CASE, proceedCaseHandler);
    yield takeEvery(UPDATE_STAGE_CASE.PROCEED_BULK_CASES, bulkCaseProceed);
    yield takeEvery(UPDATE_STAGE_CASE.REFER_CASE, referCaseHandler);
    yield takeEvery(UPDATE_STAGE_CASE.REFER_BULK_CASES, bulkCaseRefer);
    yield takeEvery(UPDATE_STAGE_CASE.REJECT_CASE, rejectCaseHandler);
    yield takeEvery(UPDATE_STAGE_CASE.REJECT_BULK_CASES, bulkCaseReject);
    yield takeLatest(UPDATE_STAGE_CASE.ASSIGN_CASE, assignCaseHandler);
    yield takeLatest(UPDATE_STAGE_CASE.REASSIGN_CASE, reassignCaseHandler);
    yield takeLatest(UPDATE_STAGE_CASE.OPEN_CASE, openHoldCaseHandler);
    yield takeLatest(UPDATE_STAGE_CASE.HOLD_CASE, holdCaseHandler);
    yield takeLatest(CASE_ACTIONS.DOWNLOAD_CASES_XLSX, downloadCasesXlsx);
});

